import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Rating,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const EmployeePerformanceForm = () => {
  const { getAuthHeaders, user } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [ratings, setRatings] = useState({});
  const [kraComments, setKraComments] = useState({});
  const [overallComment, setOverallComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Meeting response states
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [alternateDate, setAlternateDate] = useState('');
  const [alternateTime, setAlternateTime] = useState('');
  const [respondingToMeeting, setRespondingToMeeting] = useState(false);

  useEffect(() => {
    fetchMyForms();
  }, []);

  const fetchMyForms = async () => {
    try {
      const response = await axios.get(`${API_URL}/performance/my-forms`, {
        headers: getAuthHeaders()
      });
      setForms(response.data.forms);
    } catch (error) {
      console.error('Error fetching forms:', error);
      toast.error('Failed to fetch performance forms');
    } finally {
      setLoading(false);
    }
  };

  const handleViewForm = async (reviewId) => {
    try {
      const response = await axios.get(`${API_URL}/performance/reviews/${reviewId}`, {
        headers: getAuthHeaders()
      });
      const review = response.data.review;
      console.log('📋 Employee Review Data:', {
        status: review.status,
        appraisalMeeting: review.appraisalMeeting,
        employeeResponseStatus: review.appraisalMeeting?.employeeResponse?.status
      });
      setSelectedReview(review);

      // Initialize ratings from existing employee ratings or create new structure
      const initialRatings = {};
      const initialKraComments = {};
      if (review.form && review.form.kras) {
        review.form.kras.forEach((kra, kraIndex) => {
          // Get KRA comment from first KPI's comment (if exists)
          if (kra.kpis.length > 0) {
            const firstKpiIndex = 0;
            const firstKpiRating = review.employeeRatings?.find(
              r => r.kraIndex === kraIndex && r.kpiIndex === firstKpiIndex
            );
            if (firstKpiRating && firstKpiRating.comment) {
              initialKraComments[kraIndex] = firstKpiRating.comment;
            }
          }
          
          kra.kpis.forEach((kpi, kpiIndex) => {
            const key = `${kraIndex}-${kpiIndex}`;
            const existingRating = review.employeeRatings?.find(
              r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
            );
            initialRatings[key] = existingRating ? existingRating.rating : null;
          });
        });
      }
      setRatings(initialRatings);
      setKraComments(initialKraComments);
      setOverallComment(review.employeeOverallComment || '');
    } catch (error) {
      console.error('Error fetching review:', error);
      toast.error('Failed to fetch performance review');
    }
  };

  const handleRatingChange = (kraIndex, kpiIndex, value) => {
    const key = `${kraIndex}-${kpiIndex}`;
    setRatings({ ...ratings, [key]: value });
  };

  const handleSubmit = async () => {
    if (!selectedReview) return;

    // Validate all ratings are provided
    const form = selectedReview.form;
    const missingRatings = [];
    
    form.kras.forEach((kra, kraIndex) => {
      kra.kpis.forEach((kpi, kpiIndex) => {
        const key = `${kraIndex}-${kpiIndex}`;
        if (!ratings[key] || ratings[key] < 1 || ratings[key] > 5) {
          missingRatings.push(`${kra.name} - ${kpi.name}`);
        }
      });
    });

    if (missingRatings.length > 0) {
      toast.error('Please provide ratings for all KPIs');
      return;
    }

    // Convert ratings to the required format
    const ratingArray = [];
    form.kras.forEach((kra, kraIndex) => {
      const kraComment = kraComments[kraIndex] || '';
      kra.kpis.forEach((kpi, kpiIndex) => {
        const key = `${kraIndex}-${kpiIndex}`;
        // Store KRA comment in the first KPI's comment field
        ratingArray.push({
          kraIndex,
          kpiIndex,
          rating: ratings[key],
          comment: kpiIndex === 0 ? kraComment : ''
        });
      });
    });

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/employee-rating`,
        {
          ratings: ratingArray,
          overallComment
        },
        {
          headers: getAuthHeaders()
        }
      );
      toast.success('Self-assessment submitted successfully');
      setSelectedReview(null);
      fetchMyForms();
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error(error.response?.data?.message || 'Failed to submit self-assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptMeeting = async () => {
    setRespondingToMeeting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/respond-meeting`,
        { response: 'accepted' },
        { headers: getAuthHeaders() }
      );
      toast.success('Meeting accepted successfully');
      fetchMyForms();
      const updatedReview = await axios.get(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}`,
        { headers: getAuthHeaders() }
      );
      setSelectedReview(updatedReview.data.review);
    } catch (error) {
      console.error('Error accepting meeting:', error);
      toast.error(error.response?.data?.message || 'Failed to accept meeting');
    } finally {
      setRespondingToMeeting(false);
    }
  };

  const handleDeclineMeeting = async () => {
    if (!declineReason) {
      toast.error('Please provide a reason for declining');
      return;
    }

    setRespondingToMeeting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/respond-meeting`,
        {
          response: 'declined',
          declineReason,
          alternateDate: alternateDate || undefined,
          alternateTime: alternateTime || undefined
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Response submitted. Manager will be notified.');
      setShowDeclineForm(false);
      setDeclineReason('');
      setAlternateDate('');
      setAlternateTime('');
      fetchMyForms();
      const updatedReview = await axios.get(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}`,
        { headers: getAuthHeaders() }
      );
      setSelectedReview(updatedReview.data.review);
    } catch (error) {
      console.error('Error declining meeting:', error);
      toast.error(error.response?.data?.message || 'Failed to submit response');
    } finally {
      setRespondingToMeeting(false);
    }
  };

  const getStatusLabel = (form) => {
    switch (form.status) {
      case 'completed':
        return 'Completed';
      case 'meeting_conducted':
        return 'Meeting Conducted';
      case 'meeting_accepted':
        return 'Meeting Accepted';
      case 'meeting_scheduled':
        return 'Meeting Scheduled - Action Required';
      case 'meeting_scheduling_pending':
        return 'Waiting for Manager';
      case 'reschedule_required':
        return 'Waiting for Reschedule';
      case 'manager_submitted':
        return 'Manager Reviewed';
      case 'employee_submitted':
        return 'Submitted';
      default:
        return 'Pending';
    }
  };

  const getStatusColor = (form) => {
    switch (form.status) {
      case 'completed':
      case 'meeting_conducted':
        return 'success';
      case 'meeting_accepted':
        return 'info';
      case 'meeting_scheduled':
        return 'warning';
      case 'meeting_scheduling_pending':
      case 'reschedule_required':
        return 'info';
      case 'manager_submitted':
        return 'info';
      case 'employee_submitted':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (selectedReview) {
    const form = selectedReview.form;
    const isSubmitted = selectedReview.employeeSubmitted;
    const isCompleted = selectedReview.status === 'completed';

    // Calculate length of service
    const calculateLengthOfService = (dateOfJoining) => {
      if (!dateOfJoining) return 'N/A';
      const joinDate = new Date(dateOfJoining);
      const today = new Date();
      const years = today.getFullYear() - joinDate.getFullYear();
      const months = today.getMonth() - joinDate.getMonth();
      const days = today.getDate() - joinDate.getDate();
      
      let totalMonths = years * 12 + months;
      if (days < 0) totalMonths -= 1;
      
      const finalYears = Math.floor(totalMonths / 12);
      const finalMonths = totalMonths % 12;
      
      if (finalYears > 0 && finalMonths > 0) {
        return `${finalYears} Year${finalYears > 1 ? 's' : ''} ${finalMonths} Month${finalMonths > 1 ? 's' : ''}`;
      } else if (finalYears > 0) {
        return `${finalYears} Year${finalYears > 1 ? 's' : ''}`;
      } else if (finalMonths > 0) {
        return `${finalMonths} Month${finalMonths > 1 ? 's' : ''}`;
      } else {
        return 'Less than a month';
      }
    };

    const employee = selectedReview.employeeId || selectedReview.employee;
    // Prioritize dateOfJoining from PersonalInformation, fallback to createdAt
    const dateOfJoining = employee?.dateOfJoining || employee?.createdAt;
    const lengthOfService = calculateLengthOfService(dateOfJoining);

    return (
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">{form.title}</Typography>
          <Button
            variant="outlined"
            onClick={() => setSelectedReview(null)}
          >
            Back to List
          </Button>
        </Box>

        {/* Employee Details Card */}
        <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Employee Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Name</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {employee?.firstName || ''} {employee?.lastName || ''}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Designation</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {employee?.jobTitle || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Department</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {employee?.department || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Appraisal Year</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {form.reviewYear}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Date of Joining</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {dateOfJoining ? new Date(dateOfJoining).toLocaleDateString('en-GB', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: 'numeric' 
                  }) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Length of Service</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {lengthOfService}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Form Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Review Cycle</Typography>
                <Typography variant="body1">{form.reviewCycle} {form.reviewYear}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  label={isCompleted ? 'Completed' : isSubmitted ? 'Submitted' : 'Pending'}
                  color={isCompleted ? 'success' : isSubmitted ? 'info' : 'warning'}
                />
              </Grid>
              {form.description && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Description</Typography>
                  <Typography variant="body1">{form.description}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {isCompleted && selectedReview.calculatedScores && (
          <Card sx={{ mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Final Performance Score: {selectedReview.calculatedScores.finalScore.toFixed(2)}/5.00
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Performance Review Table */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Performance Review Details
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 80 }}>Sr No</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 200 }}>KRA</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 300 }}>KPI / Task</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', minWidth: 150 }}>Your Rating</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 300 }}>KRA Comment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.kras.map((kra, kraIndex) => (
                    <React.Fragment key={kraIndex}>
                      {kra.kpis.map((kpi, kpiIndex) => {
                        const key = `${kraIndex}-${kpiIndex}`;
                        const currentRating = ratings[key];
                        const existingRating = selectedReview.employeeRatings?.find(
                          r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
                        );
                        const isFirstKPI = kpiIndex === 0;
                        const rowSpan = kra.kpis.length;
                        let rowNumber = 1;
                        form.kras.slice(0, kraIndex).forEach((prevKra) => {
                          rowNumber += prevKra.kpis.length;
                        });
                        rowNumber += kpiIndex;

                        return (
                          <TableRow 
                            key={kpiIndex}
                            sx={{ 
                              '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                              '&:hover': { bgcolor: 'grey.100' }
                            }}
                          >
                            <TableCell align="center" sx={{ fontWeight: 'medium' }}>
                              {rowNumber}
                            </TableCell>
                            {isFirstKPI && (
                              <TableCell 
                                rowSpan={rowSpan} 
                                sx={{ 
                                  fontWeight: 'bold',
                                  bgcolor: 'primary.50',
                                  verticalAlign: 'top',
                                  borderRight: '2px solid',
                                  borderColor: 'primary.main'
                                }}
                              >
                                {kraIndex + 1}. {kra.name}
                                {kra.description && (
                                  <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
                                    {kra.description}
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {kpi.name}
                              </Typography>
                              {kpi.description && (
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                  {kpi.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {!isSubmitted ? (
                                <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                  <Rating
                                    value={currentRating || 0}
                                    onChange={(event, newValue) => {
                                      handleRatingChange(kraIndex, kpiIndex, newValue);
                                    }}
                                    max={5}
                                    size="medium"
                                  />
                                  {currentRating && (
                                    <Typography variant="body2" color="textSecondary">
                                      {currentRating}/5
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body1" fontWeight="medium">
                                  {existingRating?.rating || 'N/A'}/5
                                </Typography>
                              )}
                            </TableCell>
                            {isFirstKPI && (
                              <TableCell
                                rowSpan={rowSpan}
                                sx={{
                                  verticalAlign: 'top',
                                  bgcolor: 'grey.50'
                                }}
                              >
                                {!isSubmitted ? (
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={Math.max(3, rowSpan * 2)}
                                    value={kraComments[kraIndex] || ''}
                                    onChange={(e) => {
                                      setKraComments({
                                        ...kraComments,
                                        [kraIndex]: e.target.value
                                      });
                                    }}
                                    placeholder={`Add your comments for ${kra.name}...`}
                                    size="small"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    {kraComments[kraIndex] || 'No comment provided'}
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {!isSubmitted && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Overall Comments</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={overallComment}
                onChange={(e) => setOverallComment(e.target.value)}
                placeholder="Add any additional comments about your performance..."
              />
            </CardContent>
          </Card>
        )}

        {isCompleted && selectedReview.employeeOverallComment && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Your Comments</Typography>
              <Typography variant="body1">{selectedReview.employeeOverallComment}</Typography>
            </CardContent>
          </Card>
        )}

        {/* Manager comments are not visible to employees */}

        {/* Meeting Invitation - Pending Response */}
        {selectedReview.status === 'meeting_scheduled' && selectedReview.appraisalMeeting?.employeeResponse?.status === 'pending' && (
          <Card sx={{  mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'warning.dark' }}>
                📅 Appraisal Meeting Scheduled - Action Required
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Your manager has scheduled an appraisal meeting to discuss your performance review.
              </Alert>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Meeting Date</Typography>
                  <Typography variant="h6">
                    {new Date(selectedReview.appraisalMeeting.proposedDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Meeting Time</Typography>
                  <Typography variant="h6">{selectedReview.appraisalMeeting.proposedTime}</Typography>
                </Grid>
              </Grid>

              {!showDeclineForm ? (
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => setShowDeclineForm(true)}
                    disabled={respondingToMeeting}
                  >
                    Decline & Suggest Alternate
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAcceptMeeting}
                    disabled={respondingToMeeting}
                    size="large"
                  >
                    {respondingToMeeting ? <CircularProgress size={24} /> : 'Accept Meeting'}
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h6" gutterBottom color="error">
                    Decline Meeting
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason for Declining *"
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                        placeholder="Please explain why you cannot attend at the proposed time..."
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Suggested Alternate Date"
                        type="date"
                        value={alternateDate}
                        onChange={(e) => setAlternateDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Suggested Alternate Time"
                        type="time"
                        value={alternateTime}
                        onChange={(e) => setAlternateTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                  <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowDeclineForm(false);
                        setDeclineReason('');
                        setAlternateDate('');
                        setAlternateTime('');
                      }}
                      disabled={respondingToMeeting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleDeclineMeeting}
                      disabled={respondingToMeeting || !declineReason}
                    >
                      {respondingToMeeting ? <CircularProgress size={24} /> : 'Submit Decline'}
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Meeting Accepted */}
        {(selectedReview.status === 'meeting_accepted' || selectedReview.status === 'completed') && 
         selectedReview.appraisalMeeting?.employeeResponse?.status === 'accepted' && (
          <Card sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300'}}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.dark' }}>
                ✅ Meeting Confirmed
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Meeting Date:</strong> {new Date(selectedReview.appraisalMeeting.proposedDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Meeting Time:</strong> {selectedReview.appraisalMeeting.proposedTime}
              </Typography>
              {selectedReview.status === 'meeting_accepted' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Your appraisal meeting is confirmed. Please ensure you're available at the scheduled time.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Completed Appraisal - Show Full Documentation */}
        {isCompleted && selectedReview.appraisalMeeting?.meetingConducted && (
          <Card sx={{ mb: 3,  border: '2px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.dark' }}>
                ✅ Appraisal Process Completed
              </Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your appraisal meeting was conducted on{' '}
                {new Date(selectedReview.appraisalMeeting.conductedAt).toLocaleDateString()}.
                Below is the documentation from your appraisal discussion.
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Manager Commitments
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedReview.appraisalMeeting.managerCommitments}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Your Commitments
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedReview.appraisalMeeting.employeeCommitments}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Final Review Decision
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedReview.appraisalMeeting.finalReviewDecision}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Overall Feedback / Summary
                  </Typography>
                  <Typography variant="body1">
                    {selectedReview.appraisalMeeting.overallFeedback}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {!isSubmitted && (
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setSelectedReview(null)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : 'Submit Self-Assessment'}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>My Performance Reviews</Typography>
      
      {forms.length === 0 ? (
        <Alert severity="info">No performance reviews assigned to you.</Alert>
      ) : (
        <Grid container spacing={3}>
          {forms.map((item) => (
            <Grid item xs={12} md={6} key={item.reviewId}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {item.form.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {item.form.reviewCycle} {item.form.reviewYear}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Manager: {item.manager?.firstName} {item.manager?.lastName}
                  </Typography>
                  <Box mt={2}>
                    <Chip
                      label={getStatusLabel(item)}
                      color={getStatusColor(item)}
                      sx={{ mr: 1 }}
                    />
                    {item.status === 'meeting_scheduled' && item.appraisalMeeting?.employeeResponse?.status === 'pending' && (
                      <Chip
                        label="Action Required"
                        color="error"
                        size="small"
                        sx={{ animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }}
                      />
                    )}
                    {item.finalScore && (
                      <Chip
                        label={`Score: ${item.finalScore.toFixed(2)}/5.00`}
                        color="primary"
                      />
                    )}
                  </Box>
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      onClick={() => handleViewForm(item.reviewId)}
                    >
                      {item.employeeSubmitted ? 'View Details' : 'Start Review'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default EmployeePerformanceForm;

